import React from 'react';

import { css } from '@emotion/css';
import { Theme } from '@mui/material';

import { ActiveNotifications } from '@/components/notifications/ActiveNotifications';
import { useQueryDateRange } from '@/hooks/query/useQueryDateRange.ts';
import { usePatientFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles';
import { useWindowSize } from '@/hooks/useWindowSize';
import HeaderMonitoring from '@/pages/patient-monitoring/HeaderMonitoring';
import { MonitoringAside } from '@/pages/patient-monitoring/MonitoringAside.tsx';
import { MonitoringDailyData } from '@/pages/patient-monitoring/MonitoringDailyData.tsx';
import { MonitoringStats } from '@/pages/patient-monitoring/MonitoringStats.tsx';
import { getDateRangeAndPeriodicity } from '@/pages/patient-monitoring/PatientMonitoringPeriod.ts';
import {
  PatientMonitoringProvider,
  useListenToQueryDateRange,
} from '@/pages/patient-monitoring/PatientMonitoringState.tsx';
import { Queries } from '@/queries/Queries';
import { useChatMessagingState } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';

export const PatientMonitoring: React.FC = () => {
  const patient = usePatientFromURL();
  const me = Queries.user.usePractitionerMe({});
  const queryData = useQueryDateRange();

  const periodAndRange = getDateRangeAndPeriodicity(queryData);
  return (
    <PatientMonitoringProvider
      periodicity={periodAndRange.periodicity}
      from={periodAndRange.from}
      to={periodAndRange.to}
      practitioner={me}
      patient={patient}
    >
      <PatientMonitoringContent />
    </PatientMonitoringProvider>
  );
};

const PatientMonitoringContent: React.FC = () => {
  const { screenSize } = useWindowSize();
  const chatIsOpen = useChatMessagingState(state => state.isOpen);
  const styles = useStyles(makeStyles);
  useListenToQueryDateRange();

  switch (true) {
    case ['XS', 'SM', 'MD', 'LG'].includes(screenSize) || chatIsOpen:
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <HeaderMonitoring />
            <MonitoringStats />
            {!chatIsOpen && (
              /* Temporary disable the asides when messaging is open. They will be moved in the future */ <div
                className={styles.inlinedAside}
              >
                <div className={styles.asideContent}>
                  <MonitoringAside />
                </div>
                <ActiveNotifications className={styles.asideContent} />
              </div>
            )}
            <MonitoringDailyData />
          </div>
        </div>
      );
    default:
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <HeaderMonitoring />
            <MonitoringStats />
            <MonitoringDailyData />
          </div>

          <aside className={styles.aside}>
            <MonitoringAside />
            <ActiveNotifications />
          </aside>
        </div>
      );
  }
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: center;
    gap: ${theme.spacing(8)};
    margin-bottom: ${theme.spacing(12)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    gap: ${theme.spacing(8)};
    min-width: 0;
  `,
  aside: css`
    display: flex;
    flex-direction: column;
    flex: 0 1 300px;
    gap: ${theme.spacing(8)};
    padding-top: ${theme.spacing(28)};
  `,
  inlinedAside: css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(8)};
    flex-wrap: wrap;
  `,
  asideContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
    flex: 1 1 0;
    height: fit-content;
  `,
  loading: css`
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  `,
});
